import { Psychology } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography
} from "@mui/material";
import { format } from "date-fns";

import { CONVERSATION_LENS } from "../../../../utils/terminology";
import type { ExistingArtifactMatch } from "../../hooks/useExistingCoachingArtifacts";
import { useCoachingContextStore } from "../../store/coachingContextStore";
import { useWizardStore } from "../../store/wizardStore";
import { buildCoachingArtifactUrl } from "../../utils/artifactUrl";
import ContextWarningAlert from "../ContextWarningAlert";

interface GenerateReportProps {
  isLoadingExamples: boolean;
  hasDataAvailable: boolean;
  onSubmit: () => void;
  existingArtifacts: ExistingArtifactMatch[];
  isCheckingExisting: boolean;
}

const DUPLICATE_PREFIX =
  "This report has already been generated for the filters you have selected. Go back to change the filters or ";
const DUPLICATE_LINK = "take a look at the existing report";
const DUPLICATE_SUFFIX = ".";

const GenerateReport = ({
  isLoadingExamples,
  hasDataAvailable,
  onSubmit,
  existingArtifacts,
  isCheckingExisting
}: GenerateReportProps) => {
  const { selectedUser, selectedLens, weekStart, weekEnd, isGenerating } = useWizardStore();
  const { getIsLoadingContext, getHasRequiredContext, getMissingContextNames } = useCoachingContextStore();

  const isLoadingContext = getIsLoadingContext();
  const hasRequiredContext = getHasRequiredContext();
  const missingContextNames = getMissingContextNames();

  const weekRangeText = `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;

  const hasExisting = existingArtifacts.length > 0;
  const showContextWarning = !isLoadingContext && !hasRequiredContext;

  const canSubmit = !isLoadingExamples
    && !isLoadingContext
    && !isCheckingExisting
    && hasDataAvailable
    && hasRequiredContext
    && !hasExisting;

  const submitLabel = isGenerating ? "Submitting..." : "Generate Report";

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Review your report filters
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }} color="text.secondary">
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>Selected User: </Box>
          {selectedUser}
        </Typography>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>Selected {CONVERSATION_LENS}: </Box>
          {selectedLens}
        </Typography>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>Selected Date Range: </Box>
          {weekRangeText}
        </Typography>
      </Stack>

      {isLoadingContext ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading performance data...
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {showContextWarning && (
            <ContextWarningAlert missingContextNames={missingContextNames} />
          )}

          {isCheckingExisting && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Checking for existing reports...
              </Typography>
            </Box>
          )}

          {hasExisting && (
            <Alert
              severity="warning"
              data-testid="generate-report-duplicate-banner"
            >
              {DUPLICATE_PREFIX}
              <Link
                href={buildCoachingArtifactUrl(existingArtifacts[0].id)}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="always"
                data-testid="generate-report-open-existing"
              >
                {DUPLICATE_LINK}
              </Link>
              {DUPLICATE_SUFFIX}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              variant="contained"
              size="large"
              onClick={onSubmit}
              disabled={!canSubmit || isGenerating}
              startIcon={<Psychology />}
              data-testid="generate-report-submit"
            >
              {submitLabel}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default GenerateReport;
